import { idadeEmMeses, type Classe, type FaixaDoPotro } from '@equiview/domain';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { IconeBusca, IconeLista, IconeTabela } from '../components/Icones.js';
import {
  Abas,
  Aguardar,
  Alternador,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  classeTd,
  classeTh,
  Etiqueta,
  EstadoVazio,
  Indicador,
  Seletor,
  Tabela,
  ThOrdenavel,
  type Direcao,
} from '../components/ui/index.js';
import {
  casaBusca,
  classeNaData,
  FAIXAS,
  faixaNaData,
  formatarData,
  formatarIdade,
  hojeIso,
  ROTULO_CLASSE,
  ROTULO_CLASSE_PLURAL,
  ROTULO_FAIXA,
  ROTULO_SEXO,
} from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Haras, Midia } from '../dados/fonte.js';

const CLASSES: Classe[] = ['potro', 'garanhao', 'egua', 'castrado'];
type Ordem = 'nome' | 'idade' | 'avaliacao';
type Visao = 'tabela' | 'cartoes';

interface Linha {
  animal: Animal;
  classe: Classe;
  faixa: FaixaDoPotro | null;
  meses: number;
  ultimaAvaliacao: string | null;
}

/** Plantel: indicadores, filtro por classe (e faixa, nos potros), busca e ordenacao. Tudo na URL. */
export function Animais() {
  const consulta = useConsulta(
    async (f) => ({
      haras: await f.haras(),
      animais: await f.listarAnimais(),
      avaliacoes: await f.avaliacoes(),
      capas: await f.capas(),
    }),
    [],
  );
  return (
    <Aguardar consulta={consulta}>
      {({ capas, ...d }) => (
        <Capas.Provider value={capas}>
          <Plantel {...d} />
        </Capas.Provider>
      )}
    </Aguardar>
  );
}

/** Foto de perfil mais recente de cada animal, para a tabela, os cartoes e a lista. */
const Capas = createContext<Map<string, Midia>>(new Map());

function CapaDoAnimal({ animal, className }: { animal: Animal; className: string }) {
  const capa = useContext(Capas).get(animal.id);
  return <FotoDaMidia midia={capa} className={className} alt={animal.name} />;
}

const CHAVE_VISAO = 'equiview.plantel.visao';
function lerVisao(): Visao {
  try {
    return localStorage.getItem(CHAVE_VISAO) === 'cartoes' ? 'cartoes' : 'tabela';
  } catch {
    return 'tabela';
  }
}

function Plantel(props: { haras: Haras | null; animais: Animal[]; avaliacoes: Avaliacao[] }) {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  if (!fonte.ficticia && !props.haras) {
    return (
      <div className="space-y-6">
        <CabecalhoPagina titulo="Plantel" meta={<span>Dados deste aparelho.</span>} />
        {equipe ? (
          <EstadoVazio
            titulo="Nenhum haras cadastrado neste aparelho"
            acao={
              <BotaoLink variante="principal" to="/haras/novo">
                Cadastrar o primeiro haras
              </BotaoLink>
            }
          >
            Comece pelo haras; depois cadastre os animais do plantel. Para ver o app com animais de exemplo, ligue os
            dados fictícios.
          </EstadoVazio>
        ) : (
          <EstadoVazio titulo="Nenhum haras liberado para você ainda">
            O plantel aparece aqui assim que a equipe Equiview liberar o seu haras. Se já foi liberado, conecte-se à internet
            para o aparelho receber os dados.
          </EstadoVazio>
        )}
      </div>
    );
  }
  return <ConteudoPlantel {...props} />;
}

function ConteudoPlantel({ haras, animais, avaliacoes }: { haras: Haras | null; animais: Animal[]; avaliacoes: Avaliacao[] }) {
  const [params, setParams] = useSearchParams();
  const classe = CLASSES.find((c) => c === params.get('classe')) ?? null;
  const faixa = FAIXAS.find((f) => f === params.get('faixa')) ?? null;
  const busca = params.get('q') ?? '';
  const ordem: Ordem = (['nome', 'idade', 'avaliacao'] as const).find((o) => o === params.get('ordem')) ?? 'nome';
  const direcao: Direcao = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const [visao, setVisao] = useState<Visao>(lerVisao);
  const campoBusca = useRef<HTMLInputElement>(null);
  const hoje = hojeIso();

  // "/" leva direto para a busca, como nos sistemas de consulta.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      if (e.key !== '/' || alvo.closest('input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      campoBusca.current?.focus();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, []);

  const equipe = usePodeCadastrar();
  const fonte = useFonte();
  const linhas = useMemo<Linha[]>(
    () =>
      // O plantel e do haras atual. Pais de fora da base (sem proprietario) nao entram.
      animais
        .filter((a) => !haras || a.owner_farm_id === haras.id)
        .map((animal) => {
        const c = classeNaData(animal, animais, hoje);
        const datas = avaliacoes.filter((a) => a.animal_id === animal.id).map((a) => a.visit_date);
        return {
          animal,
          classe: c,
          faixa: c === 'potro' ? faixaNaData(animal, hoje) : null,
          meses: idadeEmMeses(animal.birth_date, hoje),
          ultimaAvaliacao: datas.sort().at(-1) ?? null,
        };
      }),
    [animais, avaliacoes, hoje],
  );

  const mudar = (novos: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novos)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  const ordenar = (o: Ordem) =>
    mudar({ ordem: o === 'nome' ? null : o, dir: ordem === o && direcao === 'asc' ? 'desc' : null });

  const escolherVisao = (v: Visao) => {
    setVisao(v);
    try {
      localStorage.setItem(CHAVE_VISAO, v);
    } catch {
      // Sem armazenamento: a escolha vale ate recarregar.
    }
  };

  const buscados = linhas.filter((l) => casaBusca(l.animal, busca));
  const visiveis = buscados
    .filter((l) => (!classe || l.classe === classe) && (!faixa || l.faixa === faixa))
    .sort((a, b) => {
      const s = direcao === 'asc' ? 1 : -1;
      if (ordem === 'idade') return s * (a.meses - b.meses);
      if (ordem === 'avaliacao') return s * (a.ultimaAvaliacao ?? '').localeCompare(b.ultimaAvaliacao ?? '');
      return s * a.animal.name.localeCompare(b.animal.name, 'pt-BR');
    });

  // Indicadores do plantel inteiro (nao dependem do filtro).
  const umAnoAtras = `${Number(hoje.slice(0, 4)) - 1}${hoje.slice(4)}`;
  const avaliadosNoAno = linhas.filter((l) => l.ultimaAvaliacao && l.ultimaAvaliacao >= umAnoAtras).length;
  const ultimaVisita = avaliacoes.map((a) => a.visit_date).sort().at(-1);
  const potros = linhas.filter((l) => l.classe === 'potro').length;

  const limpar = () => mudar({ classe: null, faixa: null, q: null });

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        sobretitulo={
          haras ? (
            <span>
              {haras.name}
              {haras.city ? ` · ${haras.city}/${haras.uf}` : ''}
              {fonte.cadastro && (
                <Link to="/haras" className="ml-2 tracking-normal normal-case text-destaque-texto hover:underline">
                  Trocar
                </Link>
              )}
            </span>
          ) : (
            'Meu haras'
          )
        }
        titulo="Plantel"
        meta={<span>Animais cadastrados, com a classe calculada na data de hoje.</span>}
        acoes={
          equipe &&
          haras && (
            <>
              <BotaoLink to="/animais/importar">Importar planilha</BotaoLink>
              <BotaoLink variante="principal" to="/animais/novo">
                Novo animal
              </BotaoLink>
            </>
          )
        }
      />

      <section
        aria-label="Resumo do plantel"
        className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda lg:grid-cols-4"
      >
        <Indicador rotulo="Animais no plantel" valor={linhas.length} />
        <Indicador rotulo="Avaliados em 12 meses" valor={avaliadosNoAno} complemento={`de ${linhas.length}`} />
        <Indicador rotulo="Potros" valor={potros} complemento="menos de 3 anos, sem filho" />
        <Indicador rotulo="Última visita" valor={ultimaVisita ? formatarData(ultimaVisita) : '—'} />
      </section>

      <section aria-label="Animais" className="space-y-4">
        <Abas
          rotulo="Classe"
          valor={classe ?? 'todos'}
          onMudar={(v) => mudar({ classe: v === 'todos' ? null : v, faixa: null })}
          abas={[
            { valor: 'todos', rotulo: 'Todos', contagem: buscados.length },
            ...CLASSES.map((c) => ({
              valor: c,
              rotulo: ROTULO_CLASSE_PLURAL[c],
              contagem: buscados.filter((l) => l.classe === c).length,
            })),
          ]}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {classe === 'potro' && (
              <Seletor
                rotulo="Faixa de idade"
                valor={faixa}
                onMudar={(f) => mudar({ faixa: f })}
                opcoes={[null, ...FAIXAS].map((f) => ({
                  valor: f,
                  conteudo: f ? (
                    <>
                      {ROTULO_FAIXA[f]}
                      <span className="numero ml-1.5 opacity-75">{buscados.filter((l) => l.faixa === f).length}</span>
                    </>
                  ) : (
                    'Todas as idades'
                  ),
                }))}
              />
            )}
            <p className="text-sm text-apagado" aria-live="polite">
              {visiveis.length} {visiveis.length === 1 ? 'animal' : 'animais'}
            </p>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto">
            <label className="relative flex-1 sm:w-72 sm:flex-none">
              <span className="sr-only">Buscar por nome ou registro</span>
              <IconeBusca
                width={18}
                height={18}
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-apagado"
              />
              <input
                ref={campoBusca}
                type="search"
                value={busca}
                onChange={(e) => mudar({ q: e.target.value || null })}
                placeholder="Buscar nome ou registro"
                className="w-full rounded-(--radius-controle) border border-borda-forte bg-superficie py-1.5 pr-9 pl-9 text-sm outline-none placeholder:text-apagado focus:border-destaque"
              />
              <kbd className="pointer-events-none absolute top-1/2 right-2 hidden -translate-y-1/2 border border-borda px-1.5 font-mono text-xs text-apagado lg:block">
                /
              </kbd>
            </label>
            <div className="hidden md:block">
              <Alternador
                rotulo="Visualização"
                valor={visao}
                onMudar={escolherVisao}
                opcoes={[
                  { valor: 'tabela', titulo: 'Ver em tabela', rotulo: <IconeTabela width={16} height={16} /> },
                  { valor: 'cartoes', titulo: 'Ver em cartões', rotulo: <IconeLista width={16} height={16} /> },
                ]}
              />
            </div>
          </div>
        </div>

        {visiveis.length === 0 ? (
          linhas.length === 0 ? (
            <EstadoVazio
              titulo="O plantel está vazio"
              acao={
                equipe ? (
                  <BotaoLink variante="principal" to="/animais/novo">
                    Cadastrar o primeiro animal
                  </BotaoLink>
                ) : undefined
              }
            >
              Cadastre os animais deste haras. Use “Salvar e cadastrar outro” para ir mais rápido.
            </EstadoVazio>
          ) : (
            <EstadoVazio
              titulo="Nenhum animal encontrado"
              acao={
                <Botao tamanho="sm" onClick={limpar}>
                  Limpar filtros
                </Botao>
              }
            >
              Nenhum animal do plantel corresponde a esse filtro ou busca.
            </EstadoVazio>
          )
        ) : (
          <>
            <ListaCompacta linhas={visiveis} />
            <div className="hidden md:block">
              {visao === 'tabela' ? (
                <TabelaPlantel linhas={visiveis} ordem={ordem} direcao={direcao} onOrdenar={ordenar} />
              ) : (
                <Cartoes linhas={visiveis} />
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function ClasseCelula({ l }: { l: Linha }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Etiqueta tom={l.classe === 'potro' ? 'destaque' : 'neutro'}>{ROTULO_CLASSE[l.classe]}</Etiqueta>
      {l.faixa && <span className="numero text-xs text-apagado">{ROTULO_FAIXA[l.faixa]}</span>}
    </span>
  );
}

function TabelaPlantel({
  linhas,
  ordem,
  direcao,
  onOrdenar,
}: {
  linhas: Linha[];
  ordem: Ordem;
  direcao: Direcao;
  onOrdenar: (o: Ordem) => void;
}) {
  const navegar = useNavigate();
  return (
    <div className="overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie">
      <Tabela rotulo="Plantel">
        <thead>
          <tr>
            <th scope="col" className={`${classeTh} w-14`}>
              <span className="sr-only">Foto</span>
            </th>
            <ThOrdenavel ativo={ordem === 'nome'} direcao={direcao} onOrdenar={() => onOrdenar('nome')}>
              Animal
            </ThOrdenavel>
            <th scope="col" className={classeTh}>
              Registro
            </th>
            <th scope="col" className={classeTh}>
              Classe
            </th>
            <th scope="col" className={classeTh}>
              Sexo
            </th>
            <ThOrdenavel ativo={ordem === 'idade'} direcao={direcao} onOrdenar={() => onOrdenar('idade')} alinhar="direita">
              Idade
            </ThOrdenavel>
            <ThOrdenavel
              ativo={ordem === 'avaliacao'}
              direcao={direcao}
              onOrdenar={() => onOrdenar('avaliacao')}
              alinhar="direita"
            >
              Última avaliação
            </ThOrdenavel>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr
              key={l.animal.id}
              // A linha inteira abre a ficha; o link no nome e o caminho acessivel.
              onClick={() => navegar(`/animais/${l.animal.id}`)}
              className="cursor-pointer transition-colors hover:bg-destaque-fundo/60"
            >
              <td className={`${classeTd} py-1.5`}>
                <CapaDoAnimal animal={l.animal} className="size-10" />
              </td>
              <td className={classeTd}>
                <Link
                  to={`/animais/${l.animal.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-serif text-[15px] font-semibold text-marca hover:underline"
                >
                  {l.animal.name}
                </Link>
              </td>
              <td className={`${classeTd} font-mono text-[13px] text-apagado`}>{l.animal.abccmm_registry ?? '—'}</td>
              <td className={classeTd}>
                <ClasseCelula l={l} />
              </td>
              <td className={`${classeTd} text-apagado`}>{ROTULO_SEXO[l.animal.sex]}</td>
              <td className={`${classeTd} numero text-right`}>{l.animal.birth_date_estimated ? '≈ ' : ''}{formatarIdade(l.meses)}</td>
              <td className={`${classeTd} numero text-right text-apagado`}>
                {l.ultimaAvaliacao ? formatarData(l.ultimaAvaliacao) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </Tabela>
    </div>
  );
}

function Cartoes({ linhas }: { linhas: Linha[] }) {
  return (
    <ul className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
      {linhas.map((l) => (
        <li key={l.animal.id}>
          <Link
            to={`/animais/${l.animal.id}`}
            className="group block overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie transition-colors hover:border-destaque"
          >
            <CapaDoAnimal animal={l.animal} className="aspect-3/2 w-full" />
            <div className="space-y-2 border-t border-borda p-4">
              <ClasseCelula l={l} />
              <h2 className="font-serif text-lg leading-snug font-semibold text-marca group-hover:underline">
                {l.animal.name}
              </h2>
              <dl className="grid grid-cols-2 gap-x-3 text-[13px]">
                <dt className="text-apagado">Registro</dt>
                <dd className="text-right font-mono">{l.animal.abccmm_registry ?? '—'}</dd>
                <dt className="text-apagado">Idade</dt>
                <dd className="numero text-right">{l.animal.birth_date_estimated ? '≈ ' : ''}{formatarIdade(l.meses)}</dd>
                <dt className="text-apagado">Avaliação</dt>
                <dd className="numero text-right">{l.ultimaAvaliacao ? formatarData(l.ultimaAvaliacao) : '—'}</dd>
              </dl>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Celular: lista compacta, com o que importa para achar o animal. O nome tem a
 * largura toda (ate duas linhas); classe e idade descem para a linha de baixo.
 */
function ListaCompacta({ linhas }: { linhas: Linha[] }) {
  return (
    <ul className="divide-y divide-borda overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie md:hidden">
      {linhas.map((l) => (
        <li key={l.animal.id}>
          <Link to={`/animais/${l.animal.id}`} className="flex items-center gap-3 px-3 py-2.5 active:bg-destaque-fundo">
            <CapaDoAnimal animal={l.animal} className="size-12 shrink-0" />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="line-clamp-2 font-serif leading-snug font-semibold text-marca">{l.animal.name}</p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <ClasseCelula l={l} />
                <span className="numero text-xs text-apagado">
                  {l.animal.birth_date_estimated ? '≈ ' : ''}
                  {formatarIdade(l.meses)} · {l.animal.abccmm_registry ?? 'Sem registro'}
                </span>
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
